/* -----------------Globals--------------- */
import { StatusBar } from "expo-status-bar";
import { useEffect, useMemo, useState } from "react";
import { Keyboard } from "react-native";

import theme from "@configs/theme";
/* -----------------UI--------------- */
import { PaperProvider } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

/* -----------------Helpers & Hooks--------------- */
import { useThemeContext } from "@contexts/ThemeContext";
import { useAilaStore } from "@features/session/hooks/useAilaStore";
import { useQASearch } from "@features/session/hooks/useQASearch";
import { isIOS } from "@helpers/platform";

/* -----------------Types--------------- */
import type { ThemeProp } from "react-native-paper/lib/typescript/types";

import { pageViewTypes } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { AiLAMobile } from "@memorang/features/src/qa-search/AiLAMobile";

const Modal = () => {
	const { isDarkMode } = useThemeContext();

	const finalTheme = theme(isDarkMode);
	const {
		viewerId,
		examId,
		contextId,
		handleTracking,
		feedbackDialogParams,
		examName,
	} = useQASearch();

	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.aila,
		};
	}, []);
	useAnalytics(memoizedAnalyticsParams);
	const itemId = useAilaStore((state) => state.itemId);
	const selectedQuery = useAilaStore((state) => state.selectedQuery);
	const updateSelectedQuery = useAilaStore(
		(state) => state.updateSelectedQuery,
	);
	const userPrompt = useAilaStore((state) => state.userPrompt);
	const isItemAnswered = useAilaStore((state) => state.isItemAnswered);
	const originalQuestionData = useAilaStore(
		(state) => state.originalQuestionData,
	);

	const insets = useSafeAreaInsets();
	const aiAssistant = useAppStore((store) => store.app.aiAssistant);
	const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

	useEffect(() => {
		const showListener = Keyboard.addListener("keyboardWillShow", () => {
			setIsKeyboardVisible(true);
		});
		const hideListener = Keyboard.addListener("keyboardWillHide", () => {
			setIsKeyboardVisible(false);
		});

		return () => {
			showListener.remove();
			hideListener.remove();
		};
	}, []);

	return (
		<PaperProvider theme={finalTheme as unknown as ThemeProp}>
			{/* Native modals have dark backgrounds on iOS, set the status bar to light content. */}
			{isIOS && <StatusBar style="light" />}
			<AiLAMobile
				defaultQuery={selectedQuery.length > 0 ? selectedQuery : undefined}
				vectorStoreParams={{
					examId,
					itemId,
					contextId,
				}}
				handleResetDefaultQuery={() => updateSelectedQuery("")}
				userId={viewerId!}
				context={examName}
				handleTracking={handleTracking}
				feedbackDialogParams={feedbackDialogParams}
				key={itemId}
				userPrompt={userPrompt}
				isItemAnswered={isItemAnswered}
				originalQuestionData={originalQuestionData}
				bottomAppBarStyle={
					isKeyboardVisible ? undefined : { paddingBottom: insets.bottom }
				}
				assistantInfo={aiAssistant}
			/>
		</PaperProvider>
	);
};

export default Modal;
